import { useEffect } from 'react';

import { useLocation } from 'react-router';

import { clearPageContext, pageView } from '@/segment/segment';
import { PageName } from '@/shared/types/segment';

/**
 * Reports a page view, and owns the page context while the page is mounted.
 *
 * Call it once per page component. Keyed on the pathname, so a detail page
 * reports a view per record rather than one per mount.
 *
 * @param pageName - The page, or `null` while the route does not yet know what
 *   it is — a screen the user never saw should not report a view
 * @param viewKey - The record's id, for a page whose tabs are separate routes: a
 *   tab switch is a filter on one page, not a second view of it
 */
const useSegments = (pageName: PageName | null, viewKey?: string) => {
  const { pathname } = useLocation();
  const key = viewKey ?? pathname;

  useEffect(() => {
    if (!pageName) {
      return;
    }
    pageView(pageName);
    return () => clearPageContext(pageName);
  }, [pageName, key]);
};

export default useSegments;
